import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import type { ErpMetadata, NetSuiteCustomer, NewCustomer, UpdateCustomer } from '@weft/types';
import { isEmptyArray, isEmptyString, isNotEmptyArray, isNotEmptyString, isNotNullOrUndefined, uniqueExtract } from '@weft/utils';
import { useNetSuiteCustomers } from 'apps/app/src/hooks/requests/use-net-suite';
import { useDebounce } from 'apps/app/src/hooks/use-debounce';
import React, { useEffect, useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
const DEBOUNCE_DELAY = 800;

type NetSuiteCustomersDropdownProps = {
  control: Control<NewCustomer> | Control<UpdateCustomer>;
  showDropdown: boolean;
  currentErpCustomer?: ErpMetadata | null;
};

export function NetSuiteCustomersDropdown({ control, showDropdown, currentErpCustomer }: NetSuiteCustomersDropdownProps) {
  const [searchInput, setSearchValue] = useState<string | undefined>(undefined);
  const [allCustomers, setAllCustomers] = useState<NetSuiteCustomer[]>([]);
  const debouncedSearchValue = useDebounce(searchInput, DEBOUNCE_DELAY);

  const { data: { customers } = {}, isFetching } = useNetSuiteCustomers(debouncedSearchValue, { enabled: showDropdown, keepPreviousData: true });

  useEffect(() => {
    if (isNotEmptyArray(customers)) {
      setAllCustomers(prevCustomers => mergeUniqueCustomers(prevCustomers, customers));
    }
  }, [customers]);

  const customersErpData = useMemo(() => netSuiteCustomersToErpData(allCustomers), [allCustomers]);

  return (
    <Controller
      control={control as Control<NewCustomer | UpdateCustomer>}
      name="erpMetadata"
      render={({ field }) => (
        <Autocomplete
          disabled={!showDropdown}
          options={customersErpData}
          getOptionLabel={({ erpName }) => isNotNullOrUndefined(erpName) ? erpName : ''}
          getOptionDisabled={(option) => option.erpId === field.value?.erpId}
          onChange={(_, value) => field.onChange(value)}
          value={field.value}
          defaultValue={currentErpCustomer}
          renderInput={(params) => (
            <TextField
              {...field}
              {...params}
              label="ERP ID"
              placeholder="ERP customer"
              onChange={e => {
                if (isEmptyString(e.target.value)) {
                  field.onChange(null);
                }
                setSearchValue(e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isFetching ?
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                      : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
}

function mergeUniqueCustomers(existingCustomers: NetSuiteCustomer[], newCustomers: NetSuiteCustomer[]) {
  const existingIds = uniqueExtract(existingCustomers, 'id');

  return [
    ...existingCustomers,
    ...newCustomers.filter(customer => !existingIds.includes(customer.id)),
  ];
}

function netSuiteCustomersToErpData(customers: NetSuiteCustomer[] | undefined) {
  return isEmptyArray(customers)
    ? []
    : customers.map<ErpMetadata>(({ id, entityId, companyName, aliasName }) => ({
      erpId: id,
      erpName: formatErpName(entityId, companyName, aliasName),
      displayId: entityId,
    }));
}

function formatErpName(entityId: string, companyName: string, aliasName: string) {
  const formattedName = isNotEmptyString(companyName) ? companyName : aliasName;

  return isEmptyString(formattedName) ? entityId : `${entityId} ${formattedName}`;
}
